import { CloseIco, DropDownIco } from "../../assets/customIcons";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Select, Tooltip } from "antd";

import Constants from "../../constants/Common";
import { FormFieldWrapper, ITooltip } from "../index";
import { SelectModeTypes } from "../../api/Types";
import classnames from "classnames";
import styles from "./ISelect.module.scss";
import TitleFromTooltip from "../ITooltip/TitleFromTooltip";

const { Option } = Select;

export interface ISelectProps {
  className?: string;
  field?: any;
  optionsArr?: Array<any>;
  placeholder?: string;
  disabled?: boolean;
  mode?: SelectModeTypes;
  onChange?: (
    value: string | string[] | number | number[],
    option?: any,
  ) => void;
  value?: any;
  error?: FieldError | string | Merge<FieldError, FieldErrorsImpl<any>>;
  type?: string;
  isRequired?: boolean;
  allowClear?: boolean;
  isClear?: boolean;
  dropdownRender?: (originNode: ReactNode) => ReactNode;
  info?: string | JSX.Element;
  placement?: "top" | "bottom" | "left" | "right";
  autocomplete?: string;
}

export const ISelect: React.FC<ISelectProps> = ({
  className,
  field,
  optionsArr,
  placeholder,
  disabled,
  mode,
  onChange,
  value,
  error,
  type,
  isRequired,
  allowClear = true,
  isClear = false,
  dropdownRender,
  info,
  placement,
  autocomplete = "off",
}) => {
  const [isEmpty, setIsEmpty] = useState(true);
  const [isFocus, setIsFocus] = useState(false);

  useEffect(() => {
    if (field) {
      field.value || field.value === 0 ? setIsEmpty(false) : setIsEmpty(true);
    }

    if (Array.isArray(field?.value)) {
      if (field.value.length === 0) {
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
      }
    }
    if (value) {
      !(value?.value || value?.title || value?.description)
        ? setIsEmpty(true)
        : setIsEmpty(false);
    }
  }, [field?.value, value]);

  // принудительная очистка значения
  useEffect(() => {
    if (isClear) {
      setIsEmpty(true);
      if (field) {
        field.onChange(null);
      }
      if (typeof onChange === "function") {
        onChange(null);
      }
      return;
    }
  }, [isClear]);
  useEffect(() => {
    if (
      field &&
      (field?.value !== null || field?.value !== undefined) &&
      !Array.isArray(field?.value)
    ) {
      const isValue = optionsArr?.find((el) => el?.id === field.value);
      if (!isValue && optionsArr?.length) {
        field.onChange(null);
      }
    }
  }, [JSON.stringify(optionsArr), isEmpty]);

  const options = optionsArr?.map((option, index) => {
    return (
      <React.Fragment key={crypto.randomUUID()}>
        {option?.name || option?.title || option?.description ? (
          <Option value={option.id}>
            <Tooltip
              placement="top"
              title={
                <TitleFromTooltip
                  title={placeholder}
                  value={option.description || option.title || option.name}
                />
              }
              color="white"
              overlayInnerStyle={{ color: "#5F7380", fontSize: "14px" }}
            >
              <div className={styles.optionWrapper}>
                <span>{option.description || option.title || option.name}</span>
              </div>
            </Tooltip>
          </Option>
        ) : (
          ""
        )}
      </React.Fragment>
    );
  });

  const showSearch = options?.length > Constants.showSearchCount;

  const getName: string = useMemo(() => {
    if (field?.name && isFocus && autocomplete !== "on") {
      return "";
    }
    return field?.name ?? "";
  }, [field, isFocus]);

  const selectProps = {
    name: getName,
    allowClear: !isEmpty && allowClear,
    clearIcon: <CloseIco />,
    disabled: disabled,
    popupClassName: styles.selectDropDown,
    filterOption: showSearch
      ? (input, option) => {
          let title = option.children.props.title ?? "";
          if (title) {
            if (typeof title !== "string") {
              title = option.children.props.title.props?.value;
              if (typeof title !== "string") {
                title =
                  option.children.props.title.props?.children?.find(
                    (x) => x.type === "span",
                  )?.props?.children ?? "";
                if (typeof title !== "string") title = "";
              }
            }
          }

          return title.toLowerCase().includes(input.toLowerCase());
        }
      : true,
    suffixIcon: disabled ? "" : <DropDownIco />,
    tabIndex: disabled ? -1 : 0,
    placeholder: placeholder,
    notFoundContent: <div style={{ textAlign: "center" }}>Список пуст...</div>,
    mode: mode,
    removeIcon: <CloseIco />,
    showArrow: true,
    showSearch,
    status: error ? "error" : null,
    dropdownRender,

    onFocus: (e) => {
      setIsFocus(true);
    },
    onBlur: (e) => {
      setIsFocus(false);
    },
    onClear: (e) => {
      if (!showSearch) {
        setIsFocus(false);
      }
    },
  };

  // useEffect(() => {
  //   const scrollContainer = ref.current?.querySelector(".ant-select-selection-overflow");
  //   if(scrollContainer && mode === "tags") {
  //     scrollContainer.addEventListener("wheel", (evt) => {
  //       evt.preventDefault();
  //       scrollContainer.scrollLeft += evt.deltaY;
  //     });
  //   }
  // },[]);

  const renderSelect = () => {
    if (field) {
      if (mode === "multiple" && !field?.value) {
        field.value = [];
      }
      return (
        <>
          <Select
            {...field}
            {...selectProps}
            onChange={(value) => {
              // TODO вспомнить что за баг такой
              const value_ = value === undefined ? null : value;
              field.onChange(value_);
              if (typeof onChange === "function") {
                onChange(value_);
              }
              setIsFocus(false);
            }}
          >
            {optionsArr?.length > 0 && options}
          </Select>
        </>
      );
    }
    if (value) {
      return (
        <Select {...selectProps} value={value} onChange={onChange}>
          {optionsArr?.length > 0 && options}
        </Select>
      );
    }
  };

  return (
    <div className={styles.fieldWrapper}>
      <FormFieldWrapper
        error={error}
        label={placeholder}
        info={info}
        placement={placement}
        required={isRequired}
        disabled={disabled}
        isVisible={isEmpty || field?.value === 0}
        isFocused={
          isFocus || !isEmpty || field?.value === 0 || field?.value === false
        }
        className={classnames(styles.select, {
          [className]: className,
          [styles.small]: type === "small",
          [styles.noLabel]: !placeholder,
          [styles.noneTextTransform]: type === "noneTextTransform",
        })}
        labelClassName={styles.label}
      >
        {renderSelect()}
      </FormFieldWrapper>
    </div>
  );
};
